import { Menu, User, LogOut, ArrowLeft, Shield, LayoutDashboard, CreditCard, Undo2 } from "lucide-react";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { BrandLogo } from "@/components/marketing/BrandLogo";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TooltipMenuControls } from "./TooltipMenuControls";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminPanelLink } from "@/hooks/useAdminPanelLink";
import { useNavigate } from "react-router-dom";
import { Podcast } from "lucide-react";

interface AdminHeaderProps {
  onMenuClick: () => void;
}

export function AdminHeader({ onMenuClick }: AdminHeaderProps) {
  const { profile, signOut, hasRole, isImpersonating, switchBack } = useAuth();
  const navigate = useNavigate();
  const { showAdminPanel, adminPanelTo } = useAdminPanelLink();
  const isPlatformAdmin = hasRole("admin");
  const chipLabel = isPlatformAdmin ? "Admin" : "Network Admin";
  const roleLabel = isPlatformAdmin ? "Administrator" : "Network Admin";

  const initials = profile?.display_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "AD";

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-4 border-b border-border bg-background px-4 tablet:px-6">
      {/* Left: Menu button + Logo */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="desktop:hidden h-9 w-9"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <BrandLogo className="h-8 w-auto">
            <>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
                <Podcast className="h-4 w-4 text-accent-foreground" />
              </div>
              <span className="text-lg font-bold text-foreground hidden mobile:inline">PODGO</span>
            </>
          </BrandLogo>
          <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-0.5 rounded">{chipLabel}</span>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Back to Portal Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard")}
          className="hidden tablet:flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Portal
        </Button>

        {/* Notifications */}
        {/* Notifications */}
        <NotificationCenter />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.display_name || "Admin"} />
                <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex items-center gap-2 p-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{profile?.display_name || "Admin"}</span>
                <span className="text-xs text-muted-foreground">{roleLabel}</span>
              </div>
            </div>
            <DropdownMenuSeparator />
            {showAdminPanel && (
              <DropdownMenuItem onClick={() => navigate(adminPanelTo)}>
                <Shield className="mr-2 h-4 w-4" aria-hidden="true" />
                Admin Panel
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => navigate("/dashboard")}>
              <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
              Podcaster Portal
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <User className="mr-2 h-4 w-4" />
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/billing")}>
              <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
              Billing
            </DropdownMenuItem>
            <TooltipMenuControls />
            <DropdownMenuSeparator />
            {isImpersonating && (
              <>
                <DropdownMenuItem onClick={switchBack}>
                  <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Switch Back
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
